import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useModalQueue } from './ModalQueueContext';
import { apiClient } from '@/lib/api-client';
import { paymentStore } from '@/stores/paymentStore';

const shouldSkipGeoComply = (): boolean => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'localhost' || hostname === '127.0.0.1';
};

interface ScheduledOffer {
  id: number;
  title: string;
  gc_amount: number;
  sc_amount: number;
  gc_bonus_amount: number;
  sc_bonus_amount: number;
  price: number;
  original_price: number | null;
  cadence_type: string;
  resets_at: string | null;
  can_purchase: boolean;
  purchases_remaining: number;
  group_id: number | null;
  is_welcome_offer: boolean;
}

interface ScheduledOfferGroup {
  id: number;
  title: string;
  offers: ScheduledOffer[];
  is_welcome_group: boolean;
}

interface WelcomeOfferContextType {
  showWelcomeOfferModal: boolean;
  setShowWelcomeOfferModal: (show: boolean) => void;
  currentWelcomeOffer: ScheduledOffer | null;
  currentWelcomeGroup: ScheduledOfferGroup | null;
  hasMoreWelcomeOffers: boolean;
  dismissCurrentOffer: () => void;
  checkAndShowWelcomeOffer: () => Promise<void>;
}

const WelcomeOfferContext = createContext<WelcomeOfferContextType | undefined>(undefined);

export const useWelcomeOffer = () => {
  const context = useContext(WelcomeOfferContext);
  if (context === undefined) {
    throw new Error('useWelcomeOffer must be used within a WelcomeOfferProvider');
  }
  return context;
};

const WELCOME_OFFER_SHOWN_KEY = 'welcome_offer_shown_session';

export const WelcomeOfferProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, session, isGeoComplyCompliant } = useAuth();
  const { requestShow, notifyDismissed, shouldShow } = useModalQueue();
  
  // Internal queue of welcome offers (groups first, then ungrouped)
  const [welcomeGroupsQueue, setWelcomeGroupsQueue] = useState<ScheduledOfferGroup[]>([]);
  const [welcomeOffersQueue, setWelcomeOffersQueue] = useState<ScheduledOffer[]>([]);
  
  // Current offer/group being displayed
  const [currentWelcomeGroup, setCurrentWelcomeGroup] = useState<ScheduledOfferGroup | null>(null);
  const [currentWelcomeOffer, setCurrentWelcomeOffer] = useState<ScheduledOffer | null>(null);
  
  const [hasChecked, setHasChecked] = useState(false);
  const [isEligible, setIsEligible] = useState(false);
  // Track if we're temporarily hidden (e.g., for payment modal)
  const [isTemporarilyHidden, setIsTemporarilyHidden] = useState(false);
  
  // Check if there are more offers after the current one
  const hasMoreWelcomeOffers = welcomeGroupsQueue.length > 0 || welcomeOffersQueue.length > 0;

  // Load the next offer from our internal queue
  const loadNextOffer = useCallback(() => {
    // First, try to load a group
    if (welcomeGroupsQueue.length > 0) {
      const [nextGroup, ...remainingGroups] = welcomeGroupsQueue;
      setCurrentWelcomeGroup(nextGroup);
      setCurrentWelcomeOffer(null);
      setWelcomeGroupsQueue(remainingGroups);
      return true;
    }
    
    // Then, try to load an ungrouped offer
    if (welcomeOffersQueue.length > 0) {
      const [nextOffer, ...remainingOffers] = welcomeOffersQueue;
      setCurrentWelcomeOffer(nextOffer);
      setCurrentWelcomeGroup(null);
      setWelcomeOffersQueue(remainingOffers);
      return true;
    }
    
    // All offers have been shown - mark session as complete
    sessionStorage.setItem(WELCOME_OFFER_SHOWN_KEY, 'true');
    setIsEligible(false);
    return false;
  }, [welcomeGroupsQueue, welcomeOffersQueue]);
  
  // Dismiss current offer and show next (if any)
  const dismissCurrentOffer = useCallback(() => {
    // Clear temporary hide state
    setIsTemporarilyHidden(false);
    
    // Clear current offer
    setCurrentWelcomeGroup(null);
    setCurrentWelcomeOffer(null);
    
    // Notify queue that we're done with this modal
    notifyDismissed('WELCOME_OFFER');
    
    // Check if there are more offers
    // Small delay to let the modal close animation complete
    setTimeout(() => {
      if (loadNextOffer()) {
        // Request to show the next one
        if (!paymentStore.getState().isOpen) {
          requestShow('WELCOME_OFFER');
        }
      }
    }, 100);
  }, [notifyDismissed, loadNextOffer, requestShow]);
  
  // Subscribe to payment store - request show when payment closes if we have offers
  useEffect(() => {
    let wasOpen = paymentStore.getState().isOpen;
    
    const unsubscribe = paymentStore.subscribe(() => {
      const isOpen = paymentStore.getState().isOpen;
      
      // Payment just closed and we have a current offer to show
      if (wasOpen && !isOpen && isEligible && (currentWelcomeOffer || currentWelcomeGroup)) {
        requestShow('WELCOME_OFFER');
      }
      
      wasOpen = isOpen;
    });
    
    return unsubscribe;
  }, [isEligible, currentWelcomeOffer, currentWelcomeGroup, requestShow]);

  const checkAndShowWelcomeOffer = useCallback(async () => {
    if (!user || !session) {
      return;
    }

    if (!user.email_verified) {
      return;
    }

    // Check if we've already shown the welcome offer this session
    const hasShownThisSession = sessionStorage.getItem(WELCOME_OFFER_SHOWN_KEY);
    if (hasShownThisSession) {
      return;
    }

    try {
      const data = await apiClient.get('/coins/packages');
      
      if (!data || (!data.scheduledOffers && !data.success)) {
        return;
      }

      const scheduledOffers = data.scheduledOffers || { groups: [], ungrouped: [] };
      
      // Filter for welcome offers (ungrouped)
      const welcomeUngrouped = (scheduledOffers.ungrouped || []).filter(
        (offer: ScheduledOffer) => offer.is_welcome_offer && offer.can_purchase !== false && (offer.purchases_remaining ?? 1) > 0
      );
      
      // Filter for welcome groups
      const welcomeGrouped = (scheduledOffers.groups || []).filter(
        (group: ScheduledOfferGroup) => 
          group.is_welcome_group || 
          group.offers.some((o: ScheduledOffer) => o.is_welcome_offer)
      ).filter((group: ScheduledOfferGroup) => 
        group.offers.some((o: ScheduledOffer) => o.can_purchase !== false && (o.purchases_remaining ?? 1) > 0)
      );

      // If there are welcome offers available, queue them up
      if (welcomeUngrouped.length > 0 || welcomeGrouped.length > 0) {
        setIsEligible(true);
        
        // Load the first one immediately
        if (welcomeGrouped.length > 0) {
          const [firstGroup, ...remainingGroups] = welcomeGrouped;
          setCurrentWelcomeGroup(firstGroup);
          setCurrentWelcomeOffer(null);
          setWelcomeGroupsQueue(remainingGroups);
          setWelcomeOffersQueue(welcomeUngrouped);
        } else if (welcomeUngrouped.length > 0) {
          const [firstOffer, ...remainingOffers] = welcomeUngrouped;
          setCurrentWelcomeOffer(firstOffer);
          setCurrentWelcomeGroup(null);
          setWelcomeOffersQueue(remainingOffers);
        }
        
        // Request to show via queue
        if (!paymentStore.getState().isOpen) {
          requestShow('WELCOME_OFFER');
        }
      }
    } catch {
      // Silently fail - welcome offers are not critical
    }
  }, [user, session, requestShow]);

  // Check welcome offers when user logs in AND geolocation check is complete
  useEffect(() => {
    const geoComplyPassed = shouldSkipGeoComply() || isGeoComplyCompliant === true;
    
    if (user && session && user.email_verified && geoComplyPassed && !hasChecked) {
      checkAndShowWelcomeOffer();
      setHasChecked(true);
    } else if (!user) {
      // Reset when user logs out
      setHasChecked(false);
      setIsEligible(false);
      setWelcomeGroupsQueue([]);
      setWelcomeOffersQueue([]);
      setCurrentWelcomeGroup(null);
      setCurrentWelcomeOffer(null);
      if (sessionStorage.getItem(WELCOME_OFFER_SHOWN_KEY)) {
        sessionStorage.removeItem(WELCOME_OFFER_SHOWN_KEY);
      }
    } else if (user && session && !user.email_verified) {
      setHasChecked(false);
      setIsEligible(false);
    } else if (user && session && !shouldSkipGeoComply() && isGeoComplyCompliant === false) {
      setHasChecked(false);
      setIsEligible(false);
    }
  }, [user, session, isGeoComplyCompliant, hasChecked, checkAndShowWelcomeOffer]);

  // Get visibility from queue, but allow temporary hiding
  const queueShowsModal = shouldShow('WELCOME_OFFER');
  const showWelcomeOfferModal = queueShowsModal && !isTemporarilyHidden;

  // Allow temporarily hiding the modal (e.g., when opening payment)
  const setShowWelcomeOfferModal = useCallback((show: boolean) => {
    if (show) {
      setIsTemporarilyHidden(false);
    } else {
      setIsTemporarilyHidden(true);
    }
  }, []);

  const value = {
    showWelcomeOfferModal,
    setShowWelcomeOfferModal,
    currentWelcomeOffer,
    currentWelcomeGroup,
    hasMoreWelcomeOffers,
    dismissCurrentOffer,
    checkAndShowWelcomeOffer,
  };

  return (
    <WelcomeOfferContext.Provider value={value}>
      {children}
    </WelcomeOfferContext.Provider>
  );
};
